import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, DollarSign } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { formatCurrency } from '../../utils/formatters';

interface SalaryPayment {
  id: string;
  employee_name?: string;
  period_start: string;
  period_end: string;
  base_amount: number;
  commission_amount: number;
  total_amount: number;
  status: string;
}

interface EditSalaryPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (id: string, baseAmount: number, commissionAmount: number) => Promise<void>;
  payment: SalaryPayment | null;
}

const EditSalaryPaymentModal: React.FC<EditSalaryPaymentModalProps> = ({ isOpen, onClose, onSave, payment }) => {
  const [baseAmount, setBaseAmount] = useState(0);
  const [commissionAmount, setCommissionAmount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (payment) {
      setBaseAmount(payment.base_amount ?? 0);
      setCommissionAmount(payment.commission_amount ?? 0);
    }
  }, [payment, isOpen]);

  const total = (Number(baseAmount) || 0) + (Number(commissionAmount) || 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!payment || payment.status !== 'pending') return;
    setLoading(true);
    try {
      await onSave(payment.id, Number(baseAmount) || 0, Number(commissionAmount) || 0);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!payment) return null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[100] bg-black/50" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[100] w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-xl overflow-hidden animate-fade-in"
        >
          <div className="px-6 pt-6 pb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-indigo-500" />
                Editar pago
              </h2>
              <button type="button" onClick={onClose} className="p-2 -m-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{payment.employee_name} · {payment.period_start} a {payment.period_end}</p>
            {payment.status !== 'pending' && (
              <p className="text-amber-600 dark:text-amber-400 text-sm mb-4">Solo se pueden editar pagos pendientes.</p>
            )}
            <form onSubmit={handleSubmit} className="space-y-5">
              <Input label="Sueldo base" type="number" min="0" step="0.01" value={baseAmount != null ? String(baseAmount) : ''} onChange={(e) => setBaseAmount(parseFloat(e.target.value) || 0)} />
              <Input label="Comisión" type="number" min="0" step="0.01" value={commissionAmount != null ? String(commissionAmount) : ''} onChange={(e) => setCommissionAmount(parseFloat(e.target.value) || 0)} />
              <div className="p-4 rounded-xl bg-indigo-50 dark:bg-indigo-900/20">
                <p className="text-sm text-indigo-700 dark:text-indigo-300">Total</p>
                <p className="text-2xl font-bold text-indigo-900 dark:text-indigo-100">{formatCurrency(total)}</p>
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={onClose} className="flex-1">{payment.status === 'pending' ? 'Cancelar' : 'Cerrar'}</Button>
                <Button type="submit" disabled={loading || payment.status !== 'pending'} className="flex-1">{loading ? 'Guardando...' : 'Guardar'}</Button>
              </div>
            </form>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default EditSalaryPaymentModal;
